import React, { useState, useRef, useEffect, useCallback } from 'react';
import { gsap } from 'gsap';
import './OrderCollectionPage.css';
import { 
    Card,
    Metric,
    Grid,
    Col,
    TextInput,
    Button,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeaderCell,
    TableRow,
    Title,
    Dropdown,
    DropdownItem,
    Toggle,
    ToggleItem,
 } from '@tremor/react';
import SidebarComponent from '../components/SidebarComponent';
import TitleComponent from "../components/TitleComponent";
import { TrashIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { useCookies } from 'react-cookie';
import { User } from '../interfaces/AccessInterfaces';
import { OrderSources } from '../interfaces/OrderInterfaces';
import { sourceTranslate } from '../utils/translates';

export default function OrderCollectionPage() {
    const [cookies] = useCookies(['mazelog-user']);
    const token = cookies['mazelog-user']?.token_data?.access_token;
    const oauth = "Bearer " + token;
    const userProfile = cookies['mazelog-user']?.user_data?.user_profile;
    const isTransporter = userProfile === "TRANSPORTER" || userProfile === "TRANSPORTER_COLLECTER";
    const orgId = cookies["mazelog-user"]?.organization_data?.id_org;

    const [inputedOrder, setInputedOrder] = useState<string>("");
    const [inputedOrderLast, setInputedOrderLast] = useState<string>("");
    const [selectedStatus, setSelectedStatus] = useState<string>('received');
    const [selectedUser, setSelectedUser] = useState<string>("");
    const [users, setUsers] = useState<User[]>([]);
    
    const [collectMode, setCollectMode] = useState<string>('individual');
    
    // Usamos Map para preservar a ordem de inserção (objetos podem reordenar chaves numéricas)
    type SelectedOrderInfo = { count: number; source?: string };
    const [selectedOrders, setSelectedOrders] = useState<Map<string, SelectedOrderInfo>>(new Map());
    const [selectedOrdersLote, setSelectedOrdersLote] = useState<Map<string, SelectedOrderInfo>>(new Map());

    const [adding, setAdding] = useState(false);
    // idle | success | duplicate | avulso | avulso2
    const [addStatus, setAddStatus] = useState<'idle' | 'success' | 'duplicate' | 'avulso' | 'avulso2' | 'slowdown' | 'error'>('idle');
    // Dialog multiplier states
    const [showMultiplierDialog, setShowMultiplierDialog] = useState(false);
    const [multiplierValue, setMultiplierValue] = useState<number>(1);
    const addStatusTimeoutRef = useRef<number | undefined>(); // legado (poderá ser removido depois)
    const overlayRef = useRef<HTMLDivElement | null>(null);
    const overlayContentRef = useRef<HTMLDivElement | null>(null);
    const activeTimeline = useRef<gsap.core.Timeline | null>(null);
    const audioCtxRef = useRef<AudioContext | null>(null);

    const initAudio = () => {
        if (typeof window === 'undefined') return null;
        if (!audioCtxRef.current) {
            const Ctx = (window.AudioContext || (window as any).webkitAudioContext);
            if (Ctx) audioCtxRef.current = new Ctx();
        }
        return audioCtxRef.current;
    };

    const playTone = (freq: number, startOffset: number, duration: number, type: OscillatorType = 'sine', volume = 0.25) => {
        const ctx = initAudio();
        if (!ctx) return;
        const now = ctx.currentTime + startOffset;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = type;
        osc.frequency.setValueAtTime(freq, now);
        gain.gain.setValueAtTime(0, now);
        gain.gain.linearRampToValueAtTime(volume, now + 0.02);
        // pequena envolvente
        gain.gain.linearRampToValueAtTime(volume * 0.8, now + duration * 0.5);
        gain.gain.linearRampToValueAtTime(0.0001, now + duration);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now);
        osc.stop(now + duration + 0.05);
    };

    const playSuccess = () => {
        // Arpejo rápido (C maj triad + oitava) estilo positivo
        playTone(523.25, 0, 0.22, 'sine', 0.28); // C5
        playTone(659.25, 0.07, 0.2, 'sine', 0.24); // E5
        playTone(783.99, 0.13, 0.18, 'triangle', 0.22); // G5
        playTone(1046.5, 0.22, 0.28, 'sine', 0.18); // C6
    };

    const playDuplicate = () => {
        // Dois beeps descendentes com timbre mais "warning"
        playTone(880, 0, 0.16, 'square', 0.18);
        playTone(698.46, 0.12, 0.22, 'square', 0.16);
        // breve vibração grave
        playTone(110, 0, 0.4, 'sawtooth', 0.05);
    };

    const playAvulso = () => {
        // Whoosh suave + shimmer
        playTone(392, 0, 0.5, 'sine', 0.16); // G4
        playTone(587.33, 0.05, 0.55, 'triangle', 0.12); // D5
        playTone(932.33, 0.18, 0.35, 'sine', 0.10); // Bb5
        playTone(587.33, 0.48, 0.4, 'sine', 0.08); // D5 sustain
    };

    const playAvulso2 = () => {
        // Som mais "mágico" com deslize ascendente + arpejo invertido
        playTone(311.13, 0, 0.5, 'sine', 0.14); // Eb4 base
        playTone(466.16, 0.05, 0.55, 'triangle', 0.12); // Bb4
        playTone(622.25, 0.12, 0.45, 'sine', 0.12); // Db5
        playTone(830.61, 0.22, 0.42, 'triangle', 0.10); // G#5
        playTone(1244.51, 0.33, 0.38, 'sine', 0.09); // D#6
        // brilho final
        playTone(1661.22, 0.42, 0.5, 'sine', 0.07); // G#6
    };

    const playError = () => {
        // Descendente dissonante curto indicando erro
        playTone(740, 0, 0.18, 'square', 0.22);
        playTone(622, 0.12, 0.2, 'square', 0.20);
        playTone(415, 0.24, 0.34, 'sawtooth', 0.18);
        // Grave rápido para impacto
        playTone(110, 0.05, 0.5, 'sine', 0.10);
    };

    const playSlowdown = () => {
        // Dois blips suaves em ritmo de "segure" (não agressivo)
        playTone(660, 0, 0.14, 'triangle', 0.16);
        playTone(520, 0.18, 0.16, 'triangle', 0.14);
    };

    const playSoundForStatus = (status: typeof addStatus) => {
        if (status === 'success') playSuccess();
        else if (status === 'duplicate') playDuplicate();
        else if (status === 'avulso') playAvulso();
        else if (status === 'avulso2') playAvulso2();
        else if (status === 'slowdown') playSlowdown();
        else if (status === 'error') playError();
    };

    // Configuração de partículas (desativadas atualmente)
    const PARTICLE_CONFIG = { success: 18, duplicate: 12, avulso: 20, avulso2: 26, slowdown: 14, error: 22 } as const;
    const ENABLE_PARTICLES = false; // desativei conforme pedido "retire as particulas"
    const DURATION_BASE = { success: 2.3, duplicate: 1.9, avulso: 2.6, avulso2: 2.8 } as const; // segundos estimados

    // cria partículas e anima
    const spawnParticles = useCallback((type: 'success' | 'duplicate' | 'avulso' | 'avulso2' | 'slowdown' | 'error') => {
        if (!ENABLE_PARTICLES) return; // não gera partículas
        const container = overlayContentRef.current;
        if (!container) return;
        const count = PARTICLE_CONFIG[type];
        const particles: HTMLSpanElement[] = [];
        for (let i = 0; i < count; i++) {
            const p = document.createElement('span');
            p.className = 'AddOverlayParticle';
            // Cores por tipo
            let color = '#ffffff';
            if (type === 'success') {
                const greens = ['#bbf7d0','#86efac','#4ade80','#22c55e','#15803d'];
                color = greens[Math.floor(Math.random()*greens.length)];
            } else if (type === 'duplicate') {
                const yellows = ['#fef9c3','#fde68a','#fcd34d','#fbbf24','#d97706'];
                color = yellows[Math.floor(Math.random()*yellows.length)];
            } else if (type === 'avulso') {
                const indigo = ['#e0e7ff','#c7d2fe','#a5b4fc','#818cf8','#6366f1','#4f46e5'];
                color = indigo[Math.floor(Math.random()*indigo.length)];
            } else if (type === 'avulso2') {
                const oranges = ['#fff7ed','#ffedd5','#fed7aa','#fdba74','#fb923c','#f97316','#ea580c','#c2410c','#9a3412'];
                color = oranges[Math.floor(Math.random()*oranges.length)];
            } else if (type === 'error') {
                const reds = ['#fee2e2','#fecaca','#fca5a5','#f87171','#ef4444','#dc2626','#b91c1c'];
                color = reds[Math.floor(Math.random()*reds.length)];
            }
            p.style.background = color;
            p.style.left = '50%';
            p.style.top = '50%';
            container.appendChild(p);
            particles.push(p);
        }
        // animação das partículas
        particles.forEach((p) => {
            const angle = Math.random() * Math.PI * 2;
            const radius = 40 + Math.random() * 110; // px
            const tx = Math.cos(angle) * radius;
            const ty = Math.sin(angle) * radius;
            const scale = 0.4 + Math.random() * 0.9;
            if (type === 'avulso2') {
                // movimento em arco + rotação
                const midX = tx * 0.55;
                const midY = ty * 0.55 - 30 - Math.random()*30;
                const pathTl = gsap.timeline({ onComplete: () => p.remove() });
                gsap.set(p, { opacity: 0, x: 0, y: 0, scale: 0.2, rotate: 0 });
                pathTl.to(p, { opacity: 1, duration: 0.18, ease: 'power1.out' })
                      .to(p, { motionPath: { path: [{x:0,y:0},{x:midX,y:midY},{x:tx,y:ty}] }, duration: 0.95 + Math.random()*0.5, ease: 'power2.out', rotate: 90 + Math.random()*180, scale, onUpdate: () => {}, }, '<')
                      .to(p, { opacity: 0, duration: 0.3, ease: 'power1.in' }, '-=0.25');
            } else if (type === 'error') {
                // explosão mais curta + queda
                const tlp = gsap.timeline({ onComplete: () => p.remove() });
                gsap.set(p, { opacity: 0, x: 0, y: 0, scale: 0.1, rotate: 0 });
                tlp.to(p, { opacity: 1, scale: scale * 0.9, duration: 0.15, ease: 'power2.out' })
                   .to(p, { x: tx * 0.8, y: ty * 0.8, rotate: (Math.random()*140-70), duration: 0.55 + Math.random()*0.3, ease: 'power3.out' })
                   .to(p, { y: `+=${40 + Math.random()*60}`, opacity: 0, scale: 0.05, duration: 0.35, ease: 'power2.in' }, '-=0.2');
            } else {
                gsap.fromTo(p,
                    { opacity: 0, x: 0, y: 0, scale: 0.2 },
                    { opacity: 0, x: tx, y: ty, scale, duration: 0.9 + Math.random()*0.6, ease: 'power2.out', onStart: () => {
                        gsap.to(p, { opacity: 1, duration: 0.18, ease: 'power1.out' });
                    }, onComplete: () => { p.remove(); } }
                );
            }
        });
    }, []);

    // timeline por estado + disparo de som
    useEffect(() => {
        if (addStatus === 'idle') return;
        // tentar retomar contexto se estiver suspenso (ex: interação necessária em alguns browsers)
        if (audioCtxRef.current && audioCtxRef.current.state === 'suspended') {
            audioCtxRef.current.resume().catch(() => {});
        }
        playSoundForStatus(addStatus);
        const overlay = overlayRef.current;
        const content = overlayContentRef.current;
        if (!overlay || !content) return;
        // limpar timeline anterior
        activeTimeline.current?.kill();
        gsap.set(overlay, { opacity: 0 });
        gsap.set(content, { clearProps: 'all' });
        gsap.set(content, { scale: 0.85, y: 24, opacity: 0, filter: 'blur(4px)' });

        const tl = gsap.timeline({
            onComplete: () => {
                setAddStatus('idle');
            }
        });
        activeTimeline.current = tl;

        // entrada base
        tl.to(overlay, { opacity: 1, duration: 0.28, ease: 'power2.out' })
          .to(content, { opacity: 1, scale: 1, y: 0, filter: 'blur(0px)', duration: 0.55, ease: 'back.out(1.7)' }, '-=0.05');

                if (addStatus === 'success') {
            spawnParticles('success');
            tl.to(content, { scale: 1.04, duration: 0.35, ease: 'sine.inOut', yoyo: true, repeat: 1 }, '-=0.1')
              .to(content, { scale: 1.02, duration: 0.4, ease: 'power1.inOut' })
              .to(overlay, { opacity: 0, duration: 0.45, ease: 'power2.in' }, '>-0.1');
                } else if (addStatus === 'duplicate') {
            spawnParticles('duplicate');
            tl.to(content, { x: 0, duration: 0.01 })
              .to(content, { x: 10, duration: 0.08, ease: 'power1.inOut' })
              .to(content, { x: -10, duration: 0.12, ease: 'power1.inOut', repeat: 3, yoyo: true })
              .to(content, { x: 0, duration: 0.18, ease: 'power2.out' })
              .to(content, { opacity: 0.95, scale: 0.97, duration: 0.35, ease: 'power1.out' }, '-=0.15')
              .to(overlay, { opacity: 0, duration: 0.4, ease: 'power2.in' });
                } else if (addStatus === 'avulso') {
            spawnParticles('avulso');
            tl.to(content, { scale: 1.06, duration: 0.5, ease: 'sine.inOut', yoyo: true, repeat: 1 })
              .to(content, { scale: 1, duration: 0.4, ease: 'power1.out' })
              .to(content, { filter: 'brightness(1.25) saturate(1.3)', duration: 0.4, ease: 'power1.inOut' }, '-=0.4')
              .to(content, { filter: 'brightness(1) saturate(1)', duration: 0.4, ease: 'power1.inOut' })
              .to(overlay, { opacity: 0, duration: 0.55, ease: 'power2.in' }, '>-0.1');
                } else if (addStatus === 'avulso2') {
                        spawnParticles('avulso2');
                        tl.to(content, { scale: 1.1, rotate: 3, duration: 0.5, ease: 'power2.out' })
                            .to(content, { scale: 1.04, rotate: -2, duration: 0.35, ease: 'sine.inOut', yoyo: true, repeat: 1 })
                            .to(content, { scale: 1, rotate: 0, duration: 0.3, ease: 'power1.out' }, '>-0.1')
                            .to(content, { filter: 'drop-shadow(0 0 12px rgba(255,255,255,0.55)) brightness(1.15)', duration: 0.4, ease: 'power1.inOut' }, '<')
                            .to(overlay, { opacity: 0, duration: 0.55, ease: 'power2.in' }, '>-0.05');
                                                } else if (addStatus === 'slowdown') {
                                                                // Slowdown simplificado: pequeno pulse e fade
                                                                tl.to(content, { scale: 1.04, duration: 0.25, ease: 'power2.out' })
                                                                    .to(content, { scale: 1, duration: 0.25, ease: 'power2.in' })
                                                                    .to(overlay, { opacity: 0, duration: 0.4, ease: 'power2.in' }, '+=0.35');
                } else if (addStatus === 'error') {
                        spawnParticles('error');
                        tl.to(content, { x: 0, duration: 0.01 })
                            .to(content, { x: 14, duration: 0.07, ease: 'power1.inOut' })
                            .to(content, { x: -14, duration: 0.12, ease: 'power1.inOut', repeat: 4, yoyo: true })
                            .to(content, { x: 0, duration: 0.18, ease: 'power2.out' })
                            .to(content, { scale: 0.96, filter: 'brightness(0.8) saturate(1.4) contrast(1.1)', duration: 0.35, ease: 'power1.out' }, '-=0.2')
                            .to(overlay, { opacity: 0, duration: 0.5, ease: 'power2.in' }, '>-0.05');
        }
    }, [addStatus, spawnParticles]);

    const handleSubmit = async () => {
        const baseURL = process.env.REACT_APP_MAZELOG_SYSTEM_BASE_URL;
        setAdding(true);
        for (const [order, info] of selectedOrdersLote) {
            const orderCount = info.count;
            console.log("Enviando pedido em lote:", order, orderCount);
            try {
                const urlOrder = `${baseURL}/order/${order}/status`;

                const res = await axios.put(urlOrder, 
                { 
                    status: orderCount > 1 ? 'multiplier' : selectedStatus,
                    hidden_event: false,
                    user_id: selectedUser || "0",
                    observation: orderCount.toString(),
                    collecter: {
                        name: "",
                        document: "",
                        relationship: "self"
                    },
                    coordinates: {
                        latitude: "",
                        longitude: ""
                    }
                 }, 
                { headers: { accept: 'application/json', "Authorization": oauth } }
            );

            // Atualiza source no lote (se desejar visualizar antes de limpar)
            const data = res.data;
            const source = (data?.source || OrderSources.UNKNOWN) as string;
            setSelectedOrdersLote(prev => {
                const next = new Map(prev);
                const current = next.get(order);
                if (current) next.set(order, { ...current, source });
                return next;
            });

            } catch (err) {
                console.error("Erro ao enviar pedido em lote:", order, err);
            }
        }
        alert(`Status de ${selectedOrdersLote.size} pedidos atualizado com sucesso!`);
        setSelectedOrdersLote(new Map());
        setInputedOrder("");
        setAdding(false);
    }

    const addInputed2SelectedLote = async () => {
        if (inputedOrder.trim() === '' || adding) return;

        setSelectedOrdersLote(prev => {
            const next = new Map(prev);
            const current = next.get(inputedOrder)?.count || 0;
            const prevSource = next.get(inputedOrder)?.source;
            next.set(inputedOrder, { count: current + 1, source: prevSource });
            return next;
        });
        setInputedOrder("");

    }
    const addInputed2Selected = async () => {
        if (inputedOrder.trim() === '' || adding) return;
        const orderCode = inputedOrder.trim();
        setAdding(true);
        setInputedOrderLast(inputedOrder);
        const baseURL = process.env.REACT_APP_MAZELOG_SYSTEM_BASE_URL;
        const ORDER_ADD_ENDPOINT = `${baseURL}/order/${inputedOrder}/status`;
        try {
            console.log("Adicionando pedido", orderCode, "com status", selectedStatus, "e user", selectedUser);
            const res = await axios.put(ORDER_ADD_ENDPOINT, 
                { 
                    status: selectedStatus,
                    user_id: selectedUser || "0",
                    observation: "",
                    collecter: {
                        name: "",
                        document: "",
                        relationship: "self"
                    },
                    coordinates: {
                        latitude: "",
                        longitude: ""
                    }
                 }, 
                { headers: { accept: 'application/json', "Authorization": oauth } }
            );
            const data = res.data;
            const isUnknown = data?.is_unknown === true;
            const source = (data?.source || OrderSources.UNKNOWN) as string;

            // Para casos não multiplier: sempre força count = 1
            setSelectedOrders(prev => {
                const next = new Map(prev);
                next.set(orderCode, { count: 1, source });
                return next;
            });
            if (isUnknown) {
                setAddStatus('avulso2'); // unknown true => avulso2
            } else {
                setAddStatus('success');
            }
        } catch (err: any) {
            const status = err?.response?.status;
            if (status === 406) {
                // duplicado: abre dialog de multiplier SEM alterar contagem ainda
                setAddStatus('duplicate');
                // default entre 2 e 10 (se count existir usa clamp, senão 2)
                const currentCount = selectedOrders.get(orderCode)?.count || 2;
                const clamped = Math.max(2, Math.min(10, currentCount));
                setMultiplierValue(clamped);
                setShowMultiplierDialog(true);
            } else if (status === 404) {
                // avulso simples: count = 1
                setSelectedOrders(prev => {
                    const next = new Map(prev);
                    next.set(orderCode, { count: 1, source: OrderSources.UNKNOWN });
                    return next;
                });
                setAddStatus('avulso');
            } else if (status === 404) {
                // avulso simples (duplicado bloco) count = 1
                setSelectedOrders(prev => {
                    const next = new Map(prev);
                    next.set(orderCode, { count: 1, source: OrderSources.UNKNOWN });
                    return next;
                });
                setAddStatus('avulso');
            }
            else if (status === 429) {
                // slowdown: ainda registra como 1
                setSelectedOrders(prev => {
                    const next = new Map(prev);
                    next.set(orderCode, { count: 1, source: OrderSources.UNKNOWN });
                    return next;
                });
                setAddStatus('slowdown');
            }
            else {
                setAddStatus('error');
            }

        } finally {
            setAdding(false);
            setInputedOrder('');
        }
    }
        
    const handleKeypress = (e:any) => {
        if (e.keyCode === 13) {
            collectMode === 'lote' ? addInputed2SelectedLote() : addInputed2Selected();
        }
      };

    const getUsers = () => {
        const baseURL = process.env.REACT_APP_MAZELOG_AUTH_BASE_URL;
        const oauth = "Bearer " + token;

        let url = `${baseURL}/users`;
        if (isTransporter) {
        url = `${baseURL}/users/company/${orgId}`;
        }

        axios.get(url,
        {headers: {accept: 'application/json', "Authorization": oauth}}
        )
        .then((res)=>{
        console.log(res);
        setUsers(res.data.data);
        })
        .catch((err)=>{
        console.log(err);
        })
    }

    useEffect(() => {
        getUsers();
    }, []);

  return (
    <main>
        <SidebarComponent />
        <div className='OrderContent'>
            <TitleComponent />
            <div className='p-8'>
                <Card>
                    <Metric>Pedidos inseridos: {collectMode !== 'lote' ? selectedOrders.size : selectedOrdersLote.size}</Metric>
                    <br/>

                    <Grid numCols={6} className="gap-2">
                        <Col numColSpan={6}>
                            <Title>Modo de Coleta</Title>
                            <Toggle onValueChange={(value) => setCollectMode(value)} value={collectMode} color='purple' className='DateSwitch'>
                                <ToggleItem value='individual' text='Individual'>Individual</ToggleItem>
                                <ToggleItem value='lote' text='Lote'>Lote</ToggleItem>
                            </Toggle>
                        </Col>
                        <Col numColSpan={2}>
                            <Title>Código do Pedido</Title>
                            <TextInput
                                className=''
                                onChange={(e) => setInputedOrder(e.target.value)}
                                onKeyDown={handleKeypress}
                                value={inputedOrder}
                            />
                        </Col>
                        <Col numColSpan={1}>
                            <Title>Status</Title>
                            <Dropdown
                                className='max-w-md'
                                defaultValue="received"
                                onValueChange={ (value) => setSelectedStatus(value) }
                            >
                                <DropdownItem value="created" text="Criado" />
                                <DropdownItem value="transporter_allocated" text="Transportador alocado" />
                                <DropdownItem value="collected" text="Coletado" />
                                <DropdownItem value="received" text="Recebido" />
                                <DropdownItem value="en_route" text="Em rota" />
                                <DropdownItem value="delivered" text="Entregue" />
                                <DropdownItem value="cancelled" text="Cancelado" />
                                <DropdownItem value="handling" text="Em tratativa" />
                                <DropdownItem value="awaiting_return" text="Aguard. devolução" />
                                <DropdownItem value="awaiting_transfer" text="Aguard. transferência" />
                                <DropdownItem value="return_route" text="Rota devolução" />
                                <DropdownItem value="returned" text="Devolvido" />
                                <DropdownItem value="collected_not_received" text="Coletados Não Receb." />
                                <DropdownItem value="cancelled_after_received" text="Canc. após Receb." />
                                <DropdownItem className='DropTransOption' value="returned_area_not_serviced" text="Devolvido - Área não atendida" />
                                <DropdownItem className='DropTransOption' value="new_driver_allocated" text="Novo entregador alocado" />
                            </Dropdown>
                        </Col>
                        <Col numColSpan={1}>
                            <Title>Motorista</Title>
                            <div className="flex items-center justify-between">
                                <Dropdown
                                    className=""
                                    value={selectedUser}
                                    onValueChange={(value) => setSelectedUser(value)}
                                    placeholder="Selecione um usuário"
                                    >
                                    {[
                                        <DropdownItem key="none" value="" text="Nenhum" />,
                                        ...users
                                        .filter(x => x.user_profile === "TRANSPORTER" || x.user_profile === "TRANSPORTER_COLLECTER")
                                        .map((item) => (
                                            <DropdownItem
                                            key={item.id_user}
                                            value={item.id_user?.toString() ?? ""}
                                            text={item.email}
                                            />
                                        )),
                                    ]}
                                </Dropdown>
                                {/* <button
                                    type="button"
                                    className="text-sm ml-2"
                                    onClick={() => setSelectedUser("")}
                                    aria-label="Limpar motorista selecionado"
                                    >
                                    X
                                </button> */}
                            </div>
                        </Col>
                        <Col numColSpan={1} className="self-end">
                            <Button
                                className='AddOrderBtn'
                                disabled={inputedOrder === "" || addStatus !== 'idle' || adding}
                                onClick={collectMode === 'lote' ? addInputed2SelectedLote : addInputed2Selected}
                                aria-live="polite"
                            >Adicionar pedido</Button>
                        </Col>
                        <Col numColSpan={1} className="self-end">
                            <Button
                                className='AddOrderBtn'
                                disabled={Array.from(selectedOrdersLote.keys()).length < 1 || collectMode !== 'lote' || adding}
                                onClick={handleSubmit}
                            >Enviar lote</Button>
                        </Col>
                    </Grid>
                                    
                </Card>
                <div className="grid grid-cols-3 gap-4 mt-6">
                    <div className="flex-1 col-span-2">
                        <Card>
                            <Metric className='text-xl'>Pedidos</Metric>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableHeaderCell className="text-left">Pedido</TableHeaderCell>
                                        {collectMode === 'lote' ? null : <TableHeaderCell className="text-center">Canal</TableHeaderCell>}
                                        <TableHeaderCell className="text-center">Quantidade</TableHeaderCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {collectMode === 'lote' ? (
                                        Array.from(selectedOrdersLote.entries()).length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={2} className="text-center text-sm text-gray-500">
                                                    Nenhum pedido inserido.
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            Array.from(selectedOrdersLote.entries()).reverse().map(([order, info]) => (
                                                <TableRow key={order}>
                                                    <TableCell className="TableField">
                                                        {order}
                                                    </TableCell>
                                                    <TableCell className="text-center">
                                                        <span className="OrderCounter"> (x{info.count})</span>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )
                                    ) : (
                                        Array.from(selectedOrders.entries()).length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={3} className="text-center text-sm text-gray-500">
                                                    Nenhum pedido inserido.
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            Array.from(selectedOrders.entries()).reverse().map(([order, info]) => (
                                                <TableRow key={order}>
                                                    <TableCell className="TableField">
                                                        {order}
                                                    </TableCell>
                                                    <TableCell className="text-center text-xl">
                                                        <span className="ml-2 text-sm text-gray-600">{sourceTranslate[info.source ?? '-']}</span>
                                                    </TableCell>
                                                    <TableCell className="text-center">
                                                        {info.count > 1 && <span className="OrderCounter"> (x{info.count})</span>}
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )
                                    )}
                                </TableBody>
                            </Table>
                        </Card>
                    </div>
                    <div className="flex-1 col-span-1">
                        {collectMode !== 'lote' && (
                            <Card>
                                <Metric className='text-xl'>Por Canal</Metric>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableHeaderCell>Canal</TableHeaderCell>
                                            <TableHeaderCell className="text-center">Total</TableHeaderCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(() => {
                                            const acc = new Map<string, number>();
                                            selectedOrders.forEach(({ count, source }) => {
                                                const key = source ?? OrderSources.UNKNOWN;
                                                acc.set(key, (acc.get(key) || 0) + count);
                                            });
                                            const rows = Array.from(acc.entries());
                                            if (rows.length === 0) {
                                                return (
                                                    <TableRow>
                                                        <TableCell colSpan={2} className="text-center text-sm text-gray-500">
                                                            Nenhum pedido inserido.
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            }
                                            return rows.map(([source, total]) => (
                                                <TableRow key={source}>
                                                    <TableCell className="TableField">
                                                        {sourceTranslate[source] || source}
                                                    </TableCell>
                                                    <TableCell className="text-center">
                                                        {total}
                                                    </TableCell>
                                                </TableRow>
                                            ));
                                        })()}
                                    </TableBody>
                                </Table>
                            </Card>
                        )}
                    </div>
                </div>
            </div>
        </div>
        {addStatus !== 'idle' && (
            <div
                ref={overlayRef}
                className={`AddOverlay AddOverlay--${addStatus}`}
                aria-live="assertive"
                role="status"
            >
                <div ref={overlayContentRef} className='AddOverlayContent'>
                    {addStatus === 'success' && (
                        <span>
                            ✅ Pedido adicionado com sucesso. Total inseridos: { selectedOrders.size }
                        </span>
                    )}
                    {addStatus === 'duplicate' && (
                        <span>
                            ⚠️ Pedido duplicado. {inputedOrderLast && (selectedOrders.get(inputedOrderLast)?.count || 0) > 1
                                ? `total: ${selectedOrders.get(inputedOrderLast)?.count}.`
                                : ""}
                                <br/>Defina multiplicador.
                        </span>
                    )}
                    {addStatus === 'avulso' && <span>📦 Pedido avulso criado. Total inseridos: { selectedOrders.size }</span>}
                    {addStatus === 'avulso2' && <span>✨ Pedido avulso alterado. Total inseridos: { selectedOrders.size }</span>}
                    {addStatus === 'slowdown' && <span>⏱️ Você está enviando muito rápido. Aguarde um instante.</span>}
                    {addStatus === 'error' && <span>❌ Erro ao adicionar pedido</span>}
                </div>
            </div>
        )}
        {showMultiplierDialog && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60">
                <div className="bg-white rounded-md shadow-xl p-6 w-[95vw] max-w-sm">
                    <h2 className="text-lg font-semibold mb-2">Definir multiplicador</h2>
                    <p className="text-sm text-gray-600 mb-4">Informe a quantidade de unidades duplicadas.</p>
                    <input
                        type="number"
                        inputMode="numeric"
                        min={1}
                        max={10}
                        step={1}
                        value={multiplierValue}
                        onChange={(e) => {
                            const raw = e.target.value;
                            if (raw === '') return;

                            let num = parseInt(raw, 10);
                            if (Number.isNaN(num)) return;

                            // Permite digitar '1' temporariamente para formar '10'
                            if (raw === '1') {
                                setMultiplierValue(1);
                                return;
                            }

                            // Se exceder 10 (ex: 27), usa o último dígito (ex: 7)
                            if (num > 10) {
                                const lastDigit = parseInt(raw.slice(-1), 10);
                                if (!Number.isNaN(lastDigit)) {
                                    const next = Math.max(2, Math.min(10, lastDigit));
                                    setMultiplierValue(next);
                                }
                                return;
                            }

                            // Clampeia entre 2 e 10
                            num = Math.max(2, Math.min(10, num));
                            setMultiplierValue(num);
                        }}
                        onBlur={() => {
                            // Ao sair do campo, garante o range [2, 10]
                            setMultiplierValue((v) => Math.max(2, Math.min(10, v)));
                        }}
                        className="w-full border rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <div className="flex gap-3 justify-end">
                        <button
                            type="button"
                            onClick={()=> { setShowMultiplierDialog(false); setMultiplierValue(1); }}
                            className="px-4 py-2 rounded border bg-gray-100 hover:bg-gray-200 text-sm"
                        >Cancelar</button>
                        <button
                            type="button"
                            disabled={adding}
                            onClick={async ()=> {
                                if (adding) return;
                                setAdding(true);
                                try {
                                    const baseURL = process.env.REACT_APP_MAZELOG_SYSTEM_BASE_URL;
                                    const ORDER_ADD_ENDPOINT = `${baseURL}/order/${inputedOrderLast}/status`;
                                    const res = await axios.put(ORDER_ADD_ENDPOINT,
                                        {
                                            status: 'multiplier',
                                            observation: multiplierValue.toString(),
                                            hidden_event: true,
                                            collecter: { name: '', document: '', relationship: 'self' },
                                            coordinates: { latitude: '', longitude: '' }
                                        },
                                        { headers: { accept: 'application/json', Authorization: oauth } }
                                    );
                                    // Atualiza contador adicionando o multiplier selecionado
                                        const data = res.data;
                                        const source = (data?.source || OrderSources.UNKNOWN) as string;
                                    if (inputedOrderLast) {
                                        setSelectedOrders(prev => {
                                            const next = new Map(prev);
                                                next.set(inputedOrderLast, { count: multiplierValue, source }); // substitui pelo valor escolhido
                                            return next;
                                        });
                                    }
                                    setShowMultiplierDialog(false);
                                } catch (err) {
                                    console.error('Erro ao enviar multiplicador', err);
                                } finally {
                                    setAdding(false);
                                }
                            }}
                            className="px-4 py-2 rounded bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:opacity-50"
                        >Confirmar</button>
                    </div>
                </div>
            </div>
        )}
    </main>
  );
}